import { ArrowLeftIcon as ArrowLeft } from "@phosphor-icons/react/dist/csr/ArrowLeft";
import { ArrowRightIcon as ArrowRight } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { LightbulbIcon as Lightbulb } from "@phosphor-icons/react/dist/csr/Lightbulb";
import { PackageIcon as Package } from "@phosphor-icons/react/dist/csr/Package";
import { PencilLineIcon as PencilLine } from "@phosphor-icons/react/dist/csr/PencilLine";
import type { Locale } from "@zoya/i18n";
import type { ReactNode } from "react";
import { messages } from "./messages.js";

type Translate = (key: keyof typeof messages.en) => string;

type PageProps = {
  locale: Locale;
  navigate: (path: string) => void;
  t: Translate;
};

function InternalLink({
  href,
  navigate,
  className,
  children,
}: {
  href: string;
  navigate: (path: string) => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      className={className}
      onClick={(event) => {
        if (!event.ctrlKey && !event.metaKey && event.button === 0) {
          event.preventDefault();
          navigate(href);
        }
      }}
    >
      {children}
    </a>
  );
}

function DirectionalArrow({ locale }: { locale: Locale }) {
  return locale === "ar" ? (
    <ArrowLeft aria-hidden="true" />
  ) : (
    <ArrowRight aria-hidden="true" />
  );
}

export function AboutPage({ locale, navigate, t }: PageProps) {
  const steps = [
    [t("about.step1Title"), t("about.step1Body")],
    [t("about.step2Title"), t("about.step2Body")],
    [t("about.step3Title"), t("about.step3Body")],
  ];

  return (
    <article className="information-page about-page">
      <section className="information-hero about-hero">
        <div className="information-hero__copy hero-enter">
          <p className="eyebrow">{t("about.eyebrow")}</p>
          <h1>{t("about.title")}</h1>
          <p>{t("about.body")}</p>
          <InternalLink
            href="/catalog"
            navigate={navigate}
            className="button button--gold"
          >
            {t("about.explore")}
            <span className="button__icon">
              <DirectionalArrow locale={locale} />
            </span>
          </InternalLink>
        </div>
        <figure className="information-hero__media hero-enter">
          <img
            src="/media/zoya-jewelry-box.webp"
            alt=""
            width="700"
            height="600"
          />
          <figcaption>{t("about.imageCaption")}</figcaption>
        </figure>
      </section>

      <section
        className="making-story section"
        aria-labelledby="making-story-title"
      >
        <header className="information-section-heading reveal-item">
          <div>
            <p className="eyebrow">{t("about.materialEyebrow")}</p>
            <h2 id="making-story-title">{t("about.materialTitle")}</h2>
          </div>
          <p>{t("about.materialBody")}</p>
        </header>
        <ol className="making-ledger">
          {steps.map(([title, body], index) => (
            <li className="reveal-item" key={title}>
              <span aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      <section className="about-promise section reveal-item">
        <p className="eyebrow">{t("about.promiseEyebrow")}</p>
        <h2>{t("about.promiseTitle")}</h2>
        <p>{t("about.promiseBody")}</p>
        <InternalLink href="/catalog" navigate={navigate} className="text-link">
          {t("about.explore")}
          <DirectionalArrow locale={locale} />
        </InternalLink>
      </section>
    </article>
  );
}

export function ContactPage({ locale, navigate, t }: PageProps) {
  const routes = [
    {
      href: "/account/orders",
      icon: Package,
      title: t("contact.orderTitle"),
      body: t("contact.orderBody"),
      action: t("contact.orderAction"),
    },
    {
      href: "/customer-design",
      icon: PencilLine,
      title: t("contact.designTitle"),
      body: t("contact.designBody"),
      action: t("contact.designAction"),
    },
    {
      href: "/idea-to-product",
      icon: Lightbulb,
      title: t("contact.ideaTitle"),
      body: t("contact.ideaBody"),
      action: t("contact.ideaAction"),
    },
  ];

  return (
    <article className="information-page contact-page">
      <header className="contact-hero section">
        <p className="eyebrow hero-enter">{t("contact.eyebrow")}</p>
        <h1 className="hero-enter">{t("contact.title")}</h1>
        <p className="hero-enter">{t("contact.body")}</p>
      </header>

      <section
        className="contact-routes section"
        aria-label={t("contact.routes")}
      >
        {routes.map(({ href, icon: Icon, title, body, action }) => (
          <InternalLink
            href={href}
            navigate={navigate}
            className="contact-route reveal-item"
            key={href}
          >
            <span className="contact-route__icon" aria-hidden="true">
              <Icon />
            </span>
            <span className="contact-route__copy">
              <strong>{title}</strong>
              <span>{body}</span>
            </span>
            <span className="contact-route__action">
              {action}
              <DirectionalArrow locale={locale} />
            </span>
          </InternalLink>
        ))}
      </section>

      <section className="contact-prepare section reveal-item">
        <div>
          <p className="eyebrow">{t("contact.prepareEyebrow")}</p>
          <h2>{t("contact.prepareTitle")}</h2>
          <p>{t("contact.prepareBody")}</p>
        </div>
        <dl>
          <div>
            <dt>{t("contact.baseLabel")}</dt>
            <dd>{t("contact.baseValue")}</dd>
          </div>
        </dl>
      </section>
    </article>
  );
}
